import { create } from 'zustand';
import { apiClient } from '../services/apiClient';
import { UnauthorizedError } from '../services/httpErrors';
import { isTvClient } from '../utils/tvClient';
import type { ViewerMode } from '../types';
import { useViewerModeStore } from './useViewerModeStore';

type AuthStatus = 'checking' | 'authenticated' | 'anonymous' | 'unpaired';

const MEDIA_SESSION_RECHECK_INTERVAL_MS = 5_000;

interface AuthState {
    status: AuthStatus;
    credentialMode: ViewerMode | null;
    restoreSession: () => Promise<void>;
    revalidateSessionAfterMediaError: () => Promise<void>;
    login: (password: string) => Promise<boolean>;
    logout: () => Promise<void>;
    markSignedOut: () => void;
}

const isAuthDisabled = (): boolean => {
    return process.env.REACT_APP_DISABLE_AUTH === 'true';
};

// The TV must never fall back to a password form: a rejected device token is "not paired".
const signedOutStatus = (): AuthStatus => (isTvClient() ? 'unpaired' : 'anonymous');

export const useAuthStore = create<AuthState>()((set, get) => {
    let mediaSessionCheck: Promise<void> | null = null;
    let mediaSessionCheckController: AbortController | null = null;
    let nextMediaSessionCheckAt = 0;

    /** Cancels queued or in-flight media checks before the credential changes. */
    const cancelMediaSessionCheck = () => {
        mediaSessionCheckController?.abort();
        mediaSessionCheckController = null;
        mediaSessionCheck = null;
        nextMediaSessionCheckAt = 0;
    };

    // A family credential locks the viewer mode before status flips, so the first data
    // load after admission already reads the family subset.
    const admit = (credentialMode: ViewerMode) => {
        if (credentialMode === 'family') {
            useViewerModeStore.getState().lockToFamily();
        }

        set({ status: 'authenticated', credentialMode });
    };

    return {
        status: 'checking',
        credentialMode: null,

        restoreSession: async () => {
            cancelMediaSessionCheck();

            if (isAuthDisabled()) {
                // Hermetic tests only: a TV-marked browser stands in for the device credential.
                admit(isTvClient() ? 'family' : 'personal');
                return;
            }

            try {
                const credentialMode = await apiClient.getSessionStatus();
                admit(credentialMode);
            } catch {
                set({ status: signedOutStatus(), credentialMode: null });
            }
        },

        /** Coalesces native media failures into a credential check; only a 401 signs out. */
        revalidateSessionAfterMediaError: () => {
            const shouldSkipCheck = isAuthDisabled() || get().status !== 'authenticated';
            if (shouldSkipCheck) {
                return Promise.resolve();
            }

            if (mediaSessionCheck) {
                return mediaSessionCheck;
            }

            const controller = new AbortController();
            mediaSessionCheckController = controller;
            const remainingCooldownMs = nextMediaSessionCheckAt - Date.now();
            const delayMs = Math.max(0, remainingCooldownMs);
            // Queue a trailing check during the cooldown so a newly revoked credential
            // is still detected even if no more media errors arrive.
            const readyToCheck = new Promise<void>((resolve) => {
                const timer = window.setTimeout(resolve, delayMs);
                controller.signal.addEventListener('abort', () => {
                    window.clearTimeout(timer);
                    resolve();
                }, { once: true });
            });

            mediaSessionCheck = readyToCheck.then(async () => {
                if (controller.signal.aborted) {
                    return;
                }

                nextMediaSessionCheckAt = Date.now() + MEDIA_SESSION_RECHECK_INTERVAL_MS;

                try {
                    const credentialMode = await apiClient.getSessionStatus(controller.signal);
                    if (!controller.signal.aborted) {
                        admit(credentialMode);
                    }
                } catch (error) {
                    const isRejectedCredential = error instanceof UnauthorizedError && !controller.signal.aborted;
                    if (isRejectedCredential) {
                        get().markSignedOut();
                    }
                }
            }).finally(() => {
                if (mediaSessionCheckController === controller) {
                    mediaSessionCheckController = null;
                    mediaSessionCheck = null;
                }
            });

            return mediaSessionCheck;
        },

        login: async (password: string) => {
            cancelMediaSessionCheck();

            if (isAuthDisabled()) {
                admit('personal');
                return true;
            }

            const isValid = await apiClient.login(password);
            if (isValid) {
                admit('personal');
            }

            return isValid;
        },

        logout: async () => {
            cancelMediaSessionCheck();

            try {
                await apiClient.logout();
            } finally {
                set({ status: 'anonymous', credentialMode: null });
            }
        },

        markSignedOut: () => {
            cancelMediaSessionCheck();
            set({ status: signedOutStatus(), credentialMode: null });
        },
    };
});
